// client/src/components/admin/tools/SeedOptionsForm.js
// Form component for seed and large-seed options
import PropTypes from 'prop-types'
import { MATCH_FORMAT } from '../../../constants/matchFormats'

/**
 * Form for configuring seed options
 * @param {object} props - Component props
 * @param {object} props.options - Current option values
 * @param {function} props.onChange - Callback when options change
 * @param {string} props.variant - Form variant ('normal' or 'large')
 */
function SeedOptionsForm({ options, onChange, variant = 'normal' }) {
  const handleChange = (field, value) => {
    onChange({ ...options, [field]: value })
  }

  if (variant === 'large') {
    return (
      <>
        <div className="row mb-3">
          <div className="col-12 col-sm-6">
            <label className="form-label small">Tag</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={options.tag}
              onChange={(e) => handleChange('tag', e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6">
            <label className="form-label small">Players</label>
            <input
              type="number"
              className="form-control form-control-sm"
              min="30"
              max="80"
              step="2"
              value={options.numPlayers}
              onChange={(e) =>
                handleChange('numPlayers', parseInt(e.target.value, 10) || 40)
              }
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label small">Divisions per League</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min="2"
            max="10"
            value={options.divisionsPerLeague}
            onChange={(e) =>
              handleChange(
                'divisionsPerLeague',
                parseInt(e.target.value, 10) || 5,
              )
            }
          />
          <small className="text-muted">
            Creates 2 leagues with {options.divisionsPerLeague} divisions each
          </small>
        </div>
      </>
    )
  }

  // Normal seed form
  return (
    <>
      <div className="mb-3">
        <label className="form-label small">Tag</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={options.tag}
          onChange={(e) => handleChange('tag', e.target.value)}
        />
      </div>
      <div className="mb-3">
        <label className="form-label small" htmlFor="seedMatchFormat">
          Match format
        </label>
        <select
          id="seedMatchFormat"
          className="form-select form-select-sm"
          value={options.matchFormat || MATCH_FORMAT.TRADITIONAL}
          onChange={(e) => handleChange('matchFormat', e.target.value)}
        >
          <option value={MATCH_FORMAT.TRADITIONAL}>
            Traditional (Best of 3)
          </option>
          <option value={MATCH_FORMAT.PRO_SET_8}>Pro Set to 8</option>
        </select>
        <small className="text-muted">
          Applies to newly seeded leagues so you can quickly test pro-set score
          reporting and standings.
        </small>
      </div>
      {/* Grouped pill-style toggles for content types */}
      <div
        className="mb-2 option-group"
        role="group"
        aria-labelledby="content-types-label"
      >
        <div
          id="content-types-label"
          className="option-group-header small text-muted mb-2"
        >
          Content types
        </div>
        <div className="d-flex flex-wrap gap-2">
          <>
            <input
              className="btn-check"
              type="checkbox"
              id="withCompleted"
              checked={options.withCompleted}
              onChange={(e) => handleChange('withCompleted', e.target.checked)}
            />
            <label
              className="btn btn-outline-primary btn-pill"
              htmlFor="withCompleted"
            >
              Completed
            </label>
          </>
          <>
            <input
              className="btn-check"
              type="checkbox"
              id="withScheduled"
              checked={options.withScheduled}
              onChange={(e) => handleChange('withScheduled', e.target.checked)}
            />
            <label
              className="btn btn-outline-primary btn-pill"
              htmlFor="withScheduled"
            >
              Scheduled
            </label>
          </>
          <>
            <input
              className="btn-check"
              type="checkbox"
              id="withAvailability"
              checked={options.withAvailability}
              onChange={(e) =>
                handleChange('withAvailability', e.target.checked)
              }
            />
            <label
              className="btn btn-outline-primary btn-pill"
              htmlFor="withAvailability"
            >
              Availability
            </label>
          </>
        </div>
      </div>
      {/* Percent inputs: 2 per row on small+ screens */}
      <div className="row mb-2">
        <div className="col-12 col-sm-6">
          <label className="form-label">Completed %</label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            value={options.completedPercent}
            onChange={(e) =>
              handleChange(
                'completedPercent',
                parseInt(e.target.value, 10) || 0,
              )
            }
          />
        </div>
        <div className="col-12 col-sm-6">
          <label className="form-label">Scheduled %</label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            value={options.scheduledPercent}
            onChange={(e) =>
              handleChange(
                'scheduledPercent',
                parseInt(e.target.value, 10) || 0,
              )
            }
          />
        </div>
      </div>
      <div className="mb-2">
        <label className="form-label">Avail/Player</label>
        <input
          type="number"
          className="form-control"
          min="0"
          value={options.availabilityPerPlayer}
          onChange={(e) =>
            handleChange(
              'availabilityPerPlayer',
              parseInt(e.target.value, 10) || 0,
            )
          }
        />
      </div>
      <div className="mb-2">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="useNewLeagues"
            checked={options.useNewLeagues}
            onChange={(e) => handleChange('useNewLeagues', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="useNewLeagues">
            Use new leagues (Rich Seed instead of A/B/C)
          </label>
        </div>
        <div className="form-check mt-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="includeDoubles"
            checked={options.includeDoubles || false}
            onChange={(e) => handleChange('includeDoubles', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="includeDoubles">
            Include doubles league with teams
          </label>
          <small className="d-block text-muted mt-1">
            Creates a doubles league with partner teams in addition to singles
            leagues
          </small>
        </div>
      </div>
    </>
  )
}

SeedOptionsForm.propTypes = {
  options: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['normal', 'large']),
}

export default SeedOptionsForm
